import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { type ClientFollowup, type FollowupStep, type TplParam } from "../lib/api";
import { wabaApi } from "../lib/wabaApi";
import { Modal, XIcon, btnGhost, btnPrimary, inp } from "./ui";

// Célula "Follow-up" do portal: o cliente liga/desliga, cadastra os follows (tempo +
// template da janela fechada ou prompt extra), restringe o horário e escolhe a ação de
// encerramento. Modelo, intervalo, espaço entre envios e prompt geral são do global.
// Mesmo contrato de agents.followup (apps/api/.../schemas/agent.py AgentFollowup).

const DAYS = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];
const MAX_STEPS = 10;
const lbl: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: "var(--ink-secondary)" };
const help: React.CSSProperties = { fontSize: 12, color: "var(--ink-muted)", lineHeight: 1.5 };

const bodyOf = (t: any): string => (t?.components ?? []).find((c: any) => c?.type === "BODY")?.text ?? "";
const countSlots = (body: string) => Math.max(0, ...Array.from(body.matchAll(/\{\{(\d+)\}\}/g), (m) => Number(m[1])));
const toMinutes = (n: number, unit: "min" | "h") => (unit === "h" ? n * 60 : n);
const shown = (s: FollowupStep) => (s.unit === "h" ? Math.max(1, Math.round(s.after_minutes / 60)) : s.after_minutes);

export function FollowupCard({ agentId, value, onChange }: { agentId: string; value: ClientFollowup; onChange: (v: ClientFollowup) => void }) {
  const [tpl, setTpl] = useState<number | null>(null);   // índice do follow com o popup de template aberto
  const [extra, setExtra] = useState<number | null>(null);
  const set = (patch: Partial<ClientFollowup>) => onChange({ ...value, ...patch });
  const setStep = (i: number, patch: Partial<FollowupStep>) => set({ steps: value.steps.map((s, k) => (k === i ? { ...s, ...patch } : s)) });

  // Templates aprovados da conexão WhatsApp vinculada a este agente (conta do cliente).
  const { data: bound } = useQuery<{ id: string } | null>({ queryKey: ["waba-binding", agentId], queryFn: () => wabaApi.get(`/bindings/agent/${agentId}`) });
  const { data: templates = [] } = useQuery<any[]>({ queryKey: ["waba-templates", bound?.id], queryFn: () => wabaApi.get(`/connections/${bound!.id}/templates`), enabled: !!bound?.id });
  const approved = templates.filter((t) => t.status === "APPROVED");

  const addStep = () => {
    const last = value.steps[value.steps.length - 1];
    const after = last ? last.after_minutes + 60 : 60;
    set({ steps: [...value.steps, { after_minutes: after, unit: after >= 60 && after % 60 === 0 ? "h" : "min", template: null, prompt: "" }] });
  };
  const bad = (i: number) => { const s = value.steps[i]; const prev = value.steps[i - 1]; return s.after_minutes < 1 ? "Informe um tempo maior que zero." : prev && s.after_minutes <= prev.after_minutes ? `Precisa ser depois do Follow ${i}.` : null; };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ flex: 1 }}>
          <h3 style={{ fontSize: 15, fontWeight: 600 }}>Follow-up automático</h3>
          <div style={help}>O agente volta a falar com quem parou de responder, nos tempos que você definir.</div>
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
          <input type="checkbox" checked={value.enabled} onChange={(e) => set({ enabled: e.target.checked })} style={{ accentColor: "var(--ok)", width: 18, height: 18 }} />
          {value.enabled ? "Ligado" : "Desligado"}
        </label>
      </div>

      {value.enabled && (
        <>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}><span style={lbl}>Follows</span><span style={help}>· {value.steps.length}/{MAX_STEPS}</span></div>
            {value.steps.length === 0 && <div style={help}>Nenhum follow. Adicione o primeiro.</div>}
            {value.steps.map((s, i) => {
              const err = bad(i);
              return (
                <div key={i} style={{ border: `1px solid ${err ? "var(--danger)" : "var(--line)"}`, borderRadius: 10, padding: "12px 14px", background: "var(--surface-card)", display: "flex", flexDirection: "column", gap: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 99, background: "var(--brand-soft)", color: "var(--brand)" }}>Follow {i + 1}</span>
                    <span style={{ fontSize: 13 }}>Após</span>
                    <input type="number" min={1} style={{ ...inp, width: 80 }} value={shown(s)} onChange={(e) => setStep(i, { after_minutes: toMinutes(Math.max(1, Number(e.target.value) || 1), s.unit) })} />
                    <select style={{ ...inp, width: 110 }} value={s.unit} onChange={(e) => { const u = e.target.value as "min" | "h"; setStep(i, { unit: u, after_minutes: toMinutes(shown(s), u) }); }}>
                      <option value="min">minutos</option><option value="h">horas</option>
                    </select>
                    <span style={{ fontSize: 13 }}>sem resposta</span>
                    <span style={{ flex: 1 }} />
                    <button type="button" aria-label={`Remover follow ${i + 1}`} onClick={() => set({ steps: value.steps.filter((_, k) => k !== i) })} style={{ background: "none", border: "none", color: "var(--ink-muted)", cursor: "pointer" }}><XIcon size={12} /></button>
                  </div>
                  {err && <div style={{ fontSize: 12, color: "var(--danger)" }}>{err}</div>}
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button type="button" style={{ ...btnGhost, borderColor: s.template ? "var(--ok-line)" : undefined }} onClick={() => setTpl(i)}>
                      {s.template ? `Template: ${s.template.name}` : "Escolher template (janela fechada)"}
                    </button>
                    <button type="button" style={{ ...btnGhost, borderColor: s.prompt ? "var(--ok-line)" : undefined }} onClick={() => setExtra(i)}>
                      {s.prompt ? "Instrução extra ✓" : "Instrução extra pro agente"}
                    </button>
                  </div>
                </div>
              );
            })}
            {value.steps.length < MAX_STEPS && <button type="button" style={{ ...btnGhost, alignSelf: "flex-start" }} onClick={addStep}>+ Adicionar follow</button>}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
              <input type="checkbox" checked={value.schedule.enabled} onChange={(e) => set({ schedule: { ...value.schedule, enabled: e.target.checked } })} style={{ accentColor: "var(--brand)" }} />
              Só em dias e horários específicos
            </label>
            {value.schedule.enabled && (
              <div style={{ display: "flex", flexDirection: "column", gap: 8, paddingLeft: 24 }}>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {DAYS.map((d, k) => {
                    const on = value.schedule.days.includes(k);
                    return <button key={d} type="button" onClick={() => set({ schedule: { ...value.schedule, days: on ? value.schedule.days.filter((x) => x !== k) : [...value.schedule.days, k].sort() } })}
                      style={{ padding: "4px 10px", borderRadius: 99, fontSize: 12, cursor: "pointer", border: `1px solid ${on ? "var(--brand)" : "var(--line-strong)"}`, background: on ? "var(--brand-soft)" : "var(--surface-card)", color: on ? "var(--brand)" : "var(--ink-secondary)" }}>{d}</button>;
                  })}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                  das <input type="time" style={{ ...inp, width: 110 }} value={value.schedule.start} onChange={(e) => set({ schedule: { ...value.schedule, start: e.target.value } })} />
                  às <input type="time" style={{ ...inp, width: 110 }} value={value.schedule.end} onChange={(e) => set({ schedule: { ...value.schedule, end: e.target.value } })} />
                </div>
              </div>
            )}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={lbl}>Ação de encerramento</span>
            <div style={help}>O que fazer quando o último follow foi feito e o cliente não respondeu.</div>
            <select style={{ ...inp, maxWidth: 320 }} value={value.end_action} onChange={(e) => set({ end_action: e.target.value as ClientFollowup["end_action"] })}>
              <option value="nenhuma">Sem ação</option>
              <option value="crm">Enviar para o CRM</option>
              <option value="notificar">Notificar o gerente</option>
            </select>
          </div>
        </>
      )}

      {tpl !== null && value.steps[tpl] && (
        <TemplatePopup step={value.steps[tpl]} approved={approved} connected={!!bound?.id} onClose={() => setTpl(null)}
          onSave={(t) => { setStep(tpl, { template: t }); setTpl(null); }} />
      )}
      {extra !== null && value.steps[extra] && (
        <Modal title={`Instrução extra · Follow ${extra + 1}`} onClose={() => setExtra(null)}>
          <div style={help}>Vai junto com o que o agente já sabe, só neste follow. Ex.: "Ofereça agendar uma visita."</div>
          <textarea rows={5} maxLength={2000} value={value.steps[extra].prompt} onChange={(e) => setStep(extra, { prompt: e.target.value })} style={{ ...inp, resize: "none", lineHeight: 1.45 }} />
          <div style={{ display: "flex", justifyContent: "flex-end" }}><button style={btnPrimary} onClick={() => setExtra(null)}>Pronto</button></div>
        </Modal>
      )}
    </div>
  );
}

function TemplatePopup({ step, approved, connected, onSave, onClose }: {
  step: FollowupStep; approved: any[]; connected: boolean; onSave: (t: FollowupStep["template"]) => void; onClose: () => void;
}) {
  const [name, setName] = useState(step.template?.name ?? "");
  const [lang, setLang] = useState(step.template?.language ?? "pt_BR");
  const [params, setParams] = useState<TplParam[]>(step.template?.params ?? []);
  const sel = approved.find((t) => t.name === name && t.language === lang) ?? approved.find((t) => t.name === name);
  const body = bodyOf(sel);
  const slots = sel ? countSlots(body) : params.length;  // template fora da lista (carregando/saiu de aprovado): não apaga os params
  const setParam = (i: number, v: TplParam) => setParams(Array.from({ length: Math.max(params.length, i + 1) }, (_, j) => (j === i ? v : params[j] ?? "")));
  return (
    <Modal title="Mensagem quando a conversa já fechou" onClose={onClose} width={560}>
      <div style={help}>Depois de 24 h sem resposta o WhatsApp só aceita template aprovado. Escolha um da sua conta; campo "Agente" é preenchido pela IA com o histórico.</div>
      {!connected ? <div style={{ fontSize: 13, color: "var(--ink-muted)" }}>Vincule o WhatsApp a este agente na aba Conexões para escolher um template.</div> : (
        <>
          <select style={inp} value={name ? `${name}|${lang}` : ""} onChange={(e) => { const t = approved.find((x) => `${x.name}|${x.language}` === e.target.value); setName(t?.name ?? ""); setLang(t?.language ?? "pt_BR"); setParams(Array.from({ length: countSlots(bodyOf(t)) }, () => "")); }}>
            <option value="">— sem template (só prompt) —</option>
            {approved.map((t) => <option key={`${t.name}|${t.language}`} value={`${t.name}|${t.language}`}>{t.name} ({t.language})</option>)}
          </select>
          {name && !sel && <div style={{ fontSize: 12, color: "var(--warn)" }}>Template "{name}" não está entre os aprovados agora. Os valores salvos ficam como estão.</div>}
          {body && <div style={{ fontSize: 12, color: "var(--ink-secondary)", whiteSpace: "pre-wrap", padding: "8px 10px", background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 8 }}>{body}</div>}
          {Array.from({ length: slots }, (_, i) => {
            const cur = params[i] ?? "";
            const ai = typeof cur === "object";
            return (
              <div key={i} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 11, fontFamily: "var(--font-mono)", color: "var(--ink-secondary)" }}>{`{{${i + 1}}}`}</span>
                  <div style={{ display: "flex", border: "1px solid var(--line-strong)", borderRadius: 6, overflow: "hidden" }}>
                    {([["Fixo", false], ["Agente", true]] as const).map(([l, isAi]) => (
                      <button key={l} type="button" onClick={() => { if (isAi !== ai) setParam(i, isAi ? { ai: typeof cur === "string" ? cur : "" } : (cur as { ai: string }).ai); }}
                        style={{ fontSize: 11, padding: "2px 9px", cursor: "pointer", border: "none", background: isAi === ai ? "var(--brand)" : "transparent", color: isAi === ai ? "#fff" : "var(--ink-muted)" }}>{l}</button>
                    ))}
                  </div>
                </div>
                <input style={inp} maxLength={1000} placeholder={ai ? "O que o agente deve colocar aqui (ex.: nome do carro que ele viu)" : "Texto fixo"}
                  value={ai ? (cur as { ai: string }).ai : (cur as string)} onChange={(e) => setParam(i, ai ? { ai: e.target.value } : e.target.value)} />
              </div>
            );
          })}
        </>
      )}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button style={btnGhost} onClick={onClose}>Cancelar</button>
        <button style={btnPrimary} disabled={!connected} onClick={() => onSave(name ? { name, language: lang, params: params.slice(0, slots) } : null)}>Salvar</button>
      </div>
    </Modal>
  );
}
